import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { fmtDate } from '../format';
import type { Overview as O } from '../types';
import { ErrorNote, Icon, Loading, Pill, Section } from '../components/ui';
import { useCan } from '../hooks';

const STAGES: [string, string, string][] = [
  ['candidate', 'Candidate', 'Initial suitability'], ['offered', 'Offer sent', 'Initial suitability'], ['prestart', 'Pre-start', 'Initial suitability'], ['probation', 'Probation', 'Ongoing suitability'],
  ['active', 'Active', 'Ongoing suitability'], ['separating', 'Leaving', 'Separation'],
];

/** The performance and remuneration calendar (Jan–Dec). Month index 0 = January. */
const HR_YEAR: { label: string; bars: { from: number; to: number; text: string }[] }[] = [
  { label: 'Annual review', bars: [{ from: 1, to: 2, text: 'Self-assessment, review meetings' }] },
  { label: 'Calibration & hand-over', bars: [{ from: 2, to: 2, text: 'Form 5' }, { from: 3, to: 3, text: 'Ratings by 30 Apr' }] },
  { label: 'Remuneration review', bars: [{ from: 3, to: 5, text: 'Bands, pool, allocation, approval' }, { from: 6, to: 6, text: 'New rates 1 Jul' }] },
  { label: 'Mid-year check-ins', bars: [{ from: 7, to: 8, text: 'Form 4' }] },
  { label: 'Report & close', bars: [{ from: 9, to: 9, text: 'Oct' }] },
];

function HrYear({ today }: { today: string }) {
  const m = Number(today.slice(5, 7)) - 1; const d = Number(today.slice(8, 10));
  const now = ((m + (d - 1) / 31) / 12) * 100;
  return (
    <div className="year" aria-label="HR year">
      <div className="year-row year-head"><span />{['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'].map((x, i) => <span key={i}>{x}</span>)}</div>
      {HR_YEAR.map((row) => (
        <div key={row.label} className="year-row">
          <span className="yr-l">{row.label}</span>
          <span className="yr-track">
            {row.bars.map((b, i) => <span key={i} className={`yr-bar${b.to < m ? ' past' : ''}`} title={b.text} style={{ left: `${(b.from / 12) * 100}%`, width: `${((b.to - b.from + 1) / 12) * 100}%` }} />)}
            {row === HR_YEAR[HR_YEAR.length - 1] && <span className="yr-now" style={{ left: `${now}%` }}><span>Today</span></span>}
          </span>
        </div>
      ))}
    </div>
  );
}

export function Overview() {
  const can = useCan();
  const q = useQuery({ queryKey: ['overview'], queryFn: () => api.get<O>('/api/overview'), refetchInterval: 60_000 });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const o = q.data!;
  return (
    <div className="page">
      <div className="page-h"><div><p className="eyebrow">{fmtDate(o.today)}</p><h1>Staff lifecycle overview</h1></div></div>
      <Section title="Where people are in the lifecycle" aside={<span className="dim small">AS4811:2022 personnel risk lifecycle</span>}>
        <div className="pipeline">
          {STAGES.map(([k, label, phase], i) => (
            <Link key={k} to="/people" className="pipe">
              <span className={`pipe-phase${i > 0 && STAGES[i - 1]![2] === phase ? ' ghost' : ''}`}>{phase}</span>
              <span className="pipe-n">{o.stages[k] ?? 0}</span><span className="pipe-l">{label}</span>
            </Link>
          ))}
        </div>
      </Section>
      <div className="grid-2">
        <Section title="Needs action" className="hazard" aside={<Pill tone={o.counts.overdue ? 'bad' : 'ok'}>{o.counts.overdue} overdue</Pill>}>
          {o.alerts.length === 0 ? <p className="empty">Nothing overdue or due today.</p> : (
            <ul className="alerts">
              {o.alerts.map((a, i) => (
                <li key={i}><Link className="alert" to={a.link}>
                  <span className={`sev sev-${a.severity}`} /><span className="alert-body"><strong>{a.title}</strong><span>{a.detail}</span></span><span className="alert-cat">{a.category}</span>
                </Link></li>
              ))}
            </ul>
          )}
        </Section>
        <div className="stack">
          <Section title="Onboarding & offboarding" aside={<Link className="link" to="/checklists">Open checklists <Icon n="arrow" s={14} /></Link>}>
            <div className="tiles">
              <div className="tile"><span className="tile-n">{o.counts.openChecklists}</span><span>Checklists running</span></div>
              <div className="tile"><span className="tile-n tone-bad">{o.counts.overdue}</span><span>Tasks overdue</span></div>
              <div className="tile"><span className="tile-n tone-warn">{o.counts.dueThisWeek}</span><span>Due in the next 7 days</span></div>
            </div>
          </Section>
          <Section title="Documents" aside={<Link className="link" to="/documents">Open documents <Icon n="arrow" s={14} /></Link>}>
            <p><strong className="tile-n tone-warn">{o.counts.awaitingSignature}</strong> <span className="small">awaiting signature</span></p>
          </Section>
          {can('performance:read') && (
            <Section title="HR year" aside={<Link className="link" to="/performance">Performance <Icon n="arrow" s={14} /></Link>}>
              <HrYear today={o.today} />
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
